// import { useParams } from "react-router-dom";
// import { articles } from "../data/articles";
// import ReactMarkdown from "react-markdown";
// import Footer2 from "./Footer/Footer2";

// export default function ArticlePage() {
//   const { slug } = useParams();

//   const article = articles.find(
//     (item) => item.slug === slug
//   );

//   if (!article) {
//     return (
//       <div style={{ color: "white", padding: "100px" }}>
//         Article Not Found
//       </div>
//     );
//   }

//   return (
//    <>
//     <div
//       style={{
//         background: "#000",
//         minHeight: "100vh",
//         color: "white",
//       }}
//     >
//       <div
//         style={{
//           maxWidth: "900px",
//           margin: "auto",
//           padding: "120px 20px",
//         }}
//       >
//         <img
//           src={article.image}
//           alt={article.title}
//           style={{
//             width: "100%",
//             borderRadius: "20px",
//             marginBottom: "40px",
//           }}
//         />

//         <h1>{article.title}</h1>

//         <p>
//           {article.date} • {article.readTime}
//         </p>

//         <ReactMarkdown>
//           {article.content}
//         </ReactMarkdown>
//       </div>
//     </div>
//     <Footer2/>
//    </>
//   );
// }


import { useParams, useNavigate } from "react-router-dom";
import { articles } from "../data/articles";
import ReactMarkdown from "react-markdown";
import Footer2 from "./Footer/Footer2";
import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";

const ACCENT = "#1E90FF";
const ACCENT_DIM = "rgba(30,144,255,0.12)";
const ACCENT_MID = "rgba(30,144,255,0.25)";

// ─── Reading Progress Bar ──────────────────────────────────────────────────
function ReadingProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 200, damping: 30 });

  return (
    <motion.div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: "2px",
        background: ACCENT,
        scaleX,
        transformOrigin: "0%",
        zIndex: 1000,
        boxShadow: `0 0 12px ${ACCENT}`,
      }}
    />
  );
}

// ─── Markdown Component Overrides ──────────────────────────────────────────
const mdComponents = {
  h1: ({ children }) => (
    <h1 style={{
      fontFamily: "'Sora', sans-serif",
      fontWeight: 800,
      fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)",
      lineHeight: 1.15,
      letterSpacing: "-0.025em",
      color: "#fff",
      margin: "3rem 0 1.2rem",
      paddingBottom: "0.75rem",
      borderBottom: "1px solid rgba(255,255,255,0.07)",
    }}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 style={{
      fontFamily: "'Sora', sans-serif",
      fontWeight: 700,
      fontSize: "clamp(1.35rem, 2.5vw, 1.9rem)",
      lineHeight: 1.25,
      letterSpacing: "-0.02em",
      color: "#fff",
      margin: "2.8rem 0 1rem",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    }}>
      <span style={{ display: "inline-block", width: 4, height: 22, background: ACCENT, borderRadius: 2, flexShrink: 0 }} />
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 style={{
      fontFamily: "'Sora', sans-serif",
      fontWeight: 600,
      fontSize: "clamp(1.1rem, 2vw, 1.35rem)",
      lineHeight: 1.35,
      letterSpacing: "-0.01em",
      color: "rgba(255,255,255,0.9)",
      margin: "2.2rem 0 0.75rem",
    }}>{children}</h3>
  ),
  p: ({ children }) => (
    <p style={{
      fontFamily: "'Inter', sans-serif",
      fontSize: "clamp(0.97rem, 1.2vw, 1.08rem)",
      lineHeight: 1.9,
      color: "rgba(255,255,255,0.72)",
      margin: "0 0 1.5rem",
      fontWeight: 400,
    }}>{children}</p>
  ),
  strong: ({ children }) => (
    <strong style={{ color: "#fff", fontWeight: 600 }}>{children}</strong>
  ),
  em: ({ children }) => (
    <em style={{ color: "rgba(255,255,255,0.8)", fontStyle: "italic" }}>{children}</em>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      style={{
        color: ACCENT,
        textDecoration: "none",
        borderBottom: `1px solid ${ACCENT_MID}`,
        paddingBottom: "1px",
        transition: "border-color 0.2s, color 0.2s",
      }}
      onMouseEnter={e => { e.target.style.borderColor = ACCENT; }}
      onMouseLeave={e => { e.target.style.borderColor = ACCENT_MID; }}
    >
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul style={{
      fontFamily: "'Inter', sans-serif",
      margin: "0 0 1.5rem",
      paddingLeft: "0",
      listStyle: "none",
    }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{
      fontFamily: "'Inter', sans-serif",
      margin: "0 0 1.5rem",
      paddingLeft: "1.2rem",
      color: "rgba(255,255,255,0.7)",
      fontSize: "clamp(0.95rem, 1.2vw, 1.05rem)",
      lineHeight: 1.9,
    }}>{children}</ol>
  ),
  li: ({ children, ordered }) => (
    <li style={{
      fontFamily: "'Inter', sans-serif",
      color: "rgba(255,255,255,0.7)",
      fontSize: "clamp(0.95rem, 1.2vw, 1.05rem)",
      lineHeight: 1.9,
      marginBottom: "0.5rem",
      paddingLeft: ordered ? 0 : "1.4rem",
      position: "relative",
      display: ordered ? "list-item" : "block",
    }}>
      {!ordered && (
        <span style={{
          position: "absolute",
          left: 0,
          top: "0.65rem",
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: ACCENT,
        }} />
      )}
      {children}
    </li>
  ),
  blockquote: ({ children }) => (
    <blockquote style={{
      margin: "2.2rem 0",
      padding: "24px 28px",
      borderLeft: `3px solid ${ACCENT}`,
      background: "rgba(30,144,255,0.05)",
      borderRadius: "0 12px 12px 0",
      position: "relative",
    }}>
      <div style={{
        position: "absolute",
        top: 16,
        left: 20,
        fontSize: "3rem",
        lineHeight: 1,
        color: ACCENT,
        opacity: 0.2,
        fontFamily: "Georgia, serif",
        pointerEvents: "none",
      }}>"</div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: "1.05rem", color: "rgba(255,255,255,0.75)", fontStyle: "italic", lineHeight: 1.8, position: "relative" }}>
        {children}
      </div>
    </blockquote>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code style={{
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        fontSize: "0.85em",
        background: "rgba(30,144,255,0.1)",
        color: "#7cbfff",
        padding: "2px 7px",
        borderRadius: 5,
        border: "1px solid rgba(30,144,255,0.18)",
      }}>
        {children}
      </code>
    ) : (
      <pre style={{
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: 12,
        padding: "20px 24px",
        overflowX: "auto",
        margin: "1.5rem 0",
        fontSize: "0.88rem",
        lineHeight: 1.7,
        color: "rgba(255,255,255,0.75)",
      }}>
        <code>{children}</code>
      </pre>
    ),
  hr: () => (
    <div style={{
      margin: "3rem 0",
      height: 1,
      background: "linear-gradient(90deg, transparent, rgba(30,144,255,0.3), transparent)",
    }} />
  ),
  img: ({ src, alt }) => (
    <figure style={{ margin: "2.5rem 0" }}>
      <img
        src={src}
        alt={alt}
        style={{
          width: "100%",
          borderRadius: 14,
          border: "1px solid rgba(255,255,255,0.07)",
          display: "block",
        }}
      />
      {alt && (
        <figcaption style={{
          textAlign: "center",
          marginTop: 10,
          fontFamily: "'Inter', sans-serif",
          fontSize: "0.8rem",
          color: "rgba(255,255,255,0.35)",
          letterSpacing: "0.03em",
        }}>{alt}</figcaption>
      )}
    </figure>
  ),
};

// ─── Table of Contents ─────────────────────────────────────────────────────
function extractHeadings(content = "") {
  if (!content) return [];
  const regex = /^#{1,3} (.+)$/gm;
  const headings = [];
  let match;
  while ((match = regex.exec(content)) !== null) {
    const level = match[0].indexOf(" ");
    const text = match[1];
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    headings.push({ level, text, id });
  }
  return headings;
}

// ─── Share Button ──────────────────────────────────────────────────────────
function ShareBtn({ label, icon, onClick }) {
  const [clicked, setClicked] = useState(false);
  return (
    <motion.button
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.94 }}
      onClick={() => { onClick(); setClicked(true); setTimeout(() => setClicked(false), 1800); }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "9px 18px",
        borderRadius: 10,
        border: "1px solid rgba(255,255,255,0.1)",
        background: "rgba(255,255,255,0.04)",
        color: clicked ? ACCENT : "rgba(255,255,255,0.7)",
        fontFamily: "'Inter', sans-serif",
        fontSize: "0.82rem",
        fontWeight: 500,
        cursor: "pointer",
        transition: "color 0.2s, border-color 0.2s",
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      {clicked ? "Copied!" : label}
    </motion.button>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────
export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const contentRef = useRef(null);

  const article = articles.find((item) => item.slug === slug);
  const headings = extractHeadings(article?.content);
  const related = articles.filter((a) => a.slug !== slug && a.category === article?.category).slice(0, 2);
 

   
  
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!article) {
    return (
      <div style={{
        background: "#000",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 20,
        color: "white",
        fontFamily: "'Inter', sans-serif",
      }}>
        <div style={{ fontSize: "5rem", opacity: 0.15 }}>404</div>
        <h2 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: "1.5rem" }}>Article Not Found</h2>
        <motion.button
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          onClick={() => navigate(-1)}
          style={{
            marginTop: 12,
            padding: "10px 24px",
            borderRadius: 10,
            background: ACCENT,
            color: "#fff",
            border: "none",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: "0.9rem",
            cursor: "pointer",
          }}
        >
          ← Go Back
        </motion.button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: #000; }
        ::-webkit-scrollbar-thumb { background: rgba(30,144,255,0.3); border-radius: 3px; }

        .article-layout {
          display: grid;
          grid-template-columns: 1fr 260px;
          gap: 56px;
          align-items: start;
        }

        @media (max-width: 1024px) {
          .article-layout { grid-template-columns: 1fr; }
          .toc-sidebar { display: none !important; }
        }

        @media (max-width: 640px) {
          .hero-meta { flex-direction: column !important; gap: 10px !important; }
          .share-row { flex-wrap: wrap !important; }
        }

        .toc-item:hover { color: #1E90FF !important; }
        .back-btn:hover { color: #1E90FF !important; }
      `}</style>

      <ReadingProgress />

      {/* Sticky nav bar */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: scrolled ? 1 : 0, y: scrolled ? 0 : -20 }}
        transition={{ duration: 0.35 }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 900,
          background: "rgba(0,0,0,0.85)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          padding: "14px clamp(20px, 5vw, 80px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pointerEvents: scrolled ? "all" : "none",
        }}
      >
        <button
          className="back-btn"
          onClick={() => navigate(-1)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.5)",
            fontFamily: "'Inter', sans-serif",
            fontSize: "0.84rem",
            cursor: "pointer",
            transition: "color 0.2s",
          }}
        >
          ← Back to Blog
        </button>
        <span style={{
          fontFamily: "'Sora', sans-serif",
          fontWeight: 700,
          fontSize: "0.88rem",
          color: "#fff",
          maxWidth: 400,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
          {article.title}
        </span>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.78rem", color: "rgba(255,255,255,0.35)" }}>
          {article.readTime}
        </span>
      </motion.div>

      <div style={{ background: "#000", minHeight: "100vh", color: "#fff", position: "relative", overflow: "hidden" }}>

        {/* Background glow */}
        <div style={{
          position: "fixed",
          top: "10%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 800,
          height: 600,
          borderRadius: "50%",
          background: "radial-gradient(ellipse, rgba(30,144,255,0.07), transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }} />

        {/* Noise overlay */}
        <div style={{
          position: "fixed",
          inset: 0,
          opacity: 0.02,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: "200px 200px",
          pointerEvents: "none",
          zIndex: 0,
        }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: 1160, margin: "0 auto", padding: "0 clamp(20px, 5vw, 60px)" }}>

          {/* ── BACK NAV ── */}
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            style={{ paddingTop: 48, paddingBottom: 0 }}
          >
            <button
              className="back-btn"
              onClick={() => navigate(-1)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "none",
                border: "none",
                color: "rgba(255,255,255,0.45)",
                fontFamily: "'Inter', sans-serif",
                fontSize: "0.84rem",
                fontWeight: 500,
                cursor: "pointer",
                transition: "color 0.2s",
                letterSpacing: "0.01em",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M9 2L4 7L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to Blog
            </button>
          </motion.div>

          {/* ── HERO IMAGE ── */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            style={{
              marginTop: 36,
              borderRadius: 20,
              overflow: "hidden",
              position: "relative",
              aspectRatio: "16/7",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            <img
              src={article.image}
              alt={article.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            {/* Overlay gradient */}
            <div style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 50%)",
            }} />

            {/* Category badge over image */}
            <div style={{
              position: "absolute",
              top: 20,
              left: 24,
              padding: "5px 14px",
              borderRadius: 100,
              background: "rgba(0,0,0,0.65)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(30,144,255,0.3)",
              color: ACCENT,
              fontFamily: "'Inter', sans-serif",
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}>
              {article.category || "Article"}
            </div>
          </motion.div>

          {/* ── ARTICLE META ── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            style={{ marginTop: 44, marginBottom: 52 }}
          >
            <h1 style={{
              fontFamily: "'Sora', sans-serif",
              fontWeight: 800,
              fontSize: "clamp(1.9rem, 4vw, 3.4rem)",
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              color: "#fff",
              margin: "0 0 28px",
              maxWidth: 860,
            }}>
              {article.title}
            </h1>

            <div
              className="hero-meta"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 24,
                paddingBottom: 28,
                borderBottom: "1px solid rgba(255,255,255,0.07)",
              }}
            >
              {/* Author avatar placeholder */}
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${ACCENT}, #0057b3)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: "#fff",
                  fontFamily: "'Sora', sans-serif",
                  flexShrink: 0,
                }}>
                  {(article.author || "A")[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.85rem", fontWeight: 600, color: "#fff" }}>
                    {article.author || "Anthrasync Team"}
                  </div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", color: "rgba(255,255,255,0.4)" }}>
                    Author
                  </div>
                </div>
              </div>

              <div style={{ width: 1, height: 32, background: "rgba(255,255,255,0.08)" }} />

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <rect x="1" y="2" width="12" height="11" rx="2" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" />
                  <path d="M1 5.5H13" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" />
                  <path d="M4.5 1V3.5M9.5 1V3.5" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.84rem", color: "rgba(255,255,255,0.45)" }}>
                  {article.date}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="5.5" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" />
                  <path d="M7 4V7L9 9" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.84rem", color: "rgba(255,255,255,0.45)" }}>
                  {article.readTime}
                </span>
              </div>
            </div>
          </motion.div>

          {/* ── MAIN LAYOUT ── */}
          <div className="article-layout" style={{ marginBottom: 80 }}>

            {/* ── CONTENT ── */}
            <motion.div
              ref={contentRef}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
            >
              <ReactMarkdown components={mdComponents}>
                {article.content}
              </ReactMarkdown>

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <div style={{ marginTop: 48, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {article.tags.map((tag) => (
                    <span key={tag} style={{
                      padding: "5px 14px",
                      borderRadius: 100,
                      background: ACCENT_DIM,
                      border: `1px solid ${ACCENT_MID}`,
                      color: ACCENT,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      letterSpacing: "0.05em",
                    }}>
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Share row */}
              <div style={{
                marginTop: 52,
                paddingTop: 36,
                borderTop: "1px solid rgba(255,255,255,0.07)",
              }}>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.82rem", color: "rgba(255,255,255,0.35)", marginBottom: 14, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  Share this article
                </p>
                <div className="share-row" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <ShareBtn
                    label="Copy Link"
                    icon={
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M5.5 8.5L8.5 5.5M6.5 3.5L7.3 2.7a3 3 0 014.24 4.24L10.5 8M7.5 10.5l-.8.8a3 3 0 01-4.24-4.24L3.5 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                      </svg>
                    }
                    onClick={() => navigator.clipboard.writeText(window.location.href)}
                  />
                  <ShareBtn
                    label="Twitter / X"
                    icon={
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                        <path d="M11.08 1.5H13L8.94 6.2 13.5 12.5h-3.82L6.6 9.12 3.3 12.5H1.38l4.3-4.94L1 1.5h3.93l2.8 3.1L11.08 1.5zm-.68 9.9h1.06L4.38 2.58H3.24l7.16 8.82z" />
                      </svg>
                    }
                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.title)}`, "_blank")}
                  />
                  <ShareBtn
                    label="LinkedIn"
                    icon={
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                        <path d="M2.5 1a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM1 5h3v8H1V5zm4.5 0H8v1.1C8.4 5.4 9.2 5 10.1 5 12 5 13 6.2 13 8.3V13h-3V8.8c0-.8-.3-1.3-1-1.3-.6 0-1 .4-1.2 1V13H5.5V5z" />
                      </svg>
                    }
                    onClick={() => window.open(`https://linkedin.com/shareArticle?url=${encodeURIComponent(window.location.href)}`, "_blank")}
                  />
                </div>
              </div>

              {/* Related Articles */}
              {related.length > 0 && (
                <div style={{ marginTop: 64, paddingTop: 40, borderTop: "1px solid rgba(255,255,255,0.07)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
                    <div style={{ width: 4, height: 22, background: ACCENT, borderRadius: 2 }} />
                    <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "#fff" }}>
                      Related Articles
                    </span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
                    {related.map((rel) => (
                      <motion.div
                        key={rel.slug}
                        whileHover={{ y: -5, boxShadow: "0 16px 40px rgba(30,144,255,0.15)" }}
                        transition={{ duration: 0.3 }}
                        onClick={() => navigate(`/blogs/${rel.slug}`)}
                        style={{
                          cursor: "pointer",
                          borderRadius: 16,
                          overflow: "hidden",
                          border: "1px solid rgba(255,255,255,0.07)",
                          background: "rgba(255,255,255,0.025)",
                        }}
                      >
                        <div style={{ height: 140, overflow: "hidden" }}>
                          <img src={rel.image} alt={rel.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        </div>
                        <div style={{ padding: "16px 18px 20px" }}>
                          <span style={{
                            fontSize: "0.68rem",
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 700,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            color: ACCENT,
                          }}>
                            {rel.category}
                          </span>
                          <h4 style={{
                            fontFamily: "'Sora', sans-serif",
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            lineHeight: 1.35,
                            color: "#fff",
                            marginTop: 6,
                            marginBottom: 0,
                          }}>
                            {rel.title}
                          </h4>
                          <p style={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "0.78rem",
                            color: "rgba(255,255,255,0.4)",
                            marginTop: 8,
                          }}>
                            {rel.readTime}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>

            {/* ── SIDEBAR TOC ── */}
            <motion.aside
              className="toc-sidebar"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              style={{
                position: "sticky",
                top: 88,
                maxHeight: "calc(100vh - 120px)",
                overflowY: "auto",
                paddingRight: 4,
              }}
            >
              {headings.length > 0 && (
                <div style={{
                  borderRadius: 16,
                  border: "1px solid rgba(255,255,255,0.07)",
                  background: "rgba(255,255,255,0.025)",
                  backdropFilter: "blur(12px)",
                  padding: "22px 22px",
                }}>
                  <p style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.3)",
                    marginBottom: 16,
                  }}>
                    Contents
                  </p>
                  {headings.map((h, i) => (
                    <a
                      key={i}
                      href={`#${h.id}`}
                      className="toc-item"
                      style={{
                        display: "block",
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "0.8rem",
                        lineHeight: 1.5,
                        color: "rgba(255,255,255,0.45)",
                        textDecoration: "none",
                        paddingLeft: h.level === 1 ? 0 : h.level === 2 ? 10 : 20,
                        paddingTop: 6,
                        paddingBottom: 6,
                        borderLeft: h.level === 1 ? "none" : "1px solid rgba(255,255,255,0.07)",
                        transition: "color 0.2s, border-color 0.2s",
                        fontWeight: h.level === 1 ? 600 : 400,
                      }}
                    >
                      {h.text}
                    </a>
                  ))}
                </div>
              )}

              {/* Article stats */}
              <div style={{
                marginTop: 16,
                borderRadius: 16,
                border: "1px solid rgba(255,255,255,0.07)",
                background: "rgba(255,255,255,0.025)",
                padding: "20px 22px",
              }}>
                <p style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.3)",
                  marginBottom: 14,
                }}>
                  Article Info
                </p>
                {[
                  ["Category", article.category || "—"],
                  ["Read Time", article.readTime],
                  ["Published", article.date],
                ].map(([label, val]) => (
                  <div key={label} style={{ marginBottom: 10 }}>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: "rgba(255,255,255,0.3)", marginBottom: 2 }}>
                      {label}
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.84rem", color: "rgba(255,255,255,0.75)", fontWeight: 500 }}>
                      {val}
                    </div>
                  </div>
                ))}
              </div>
            </motion.aside>
          </div>
        </div>

        <Footer2 />
      </div>
    </>
  );
}